// An admin can open a short-lived session as a student or partner so they can
// see the portal from that side. The session is deliberately kept in
// sessionStorage rather than localStorage: localStorage is shared by every tab
// on the origin, so writing there would silently replace the admin's own token
// in the tab they are working in. sessionStorage is per-tab, so the impersonated
// tab ends with the tab and the admin keeps their own session.

const TOKEN_KEY = "taskpart_auth_token";
const IMPERSONATION_KEY = "taskpart_impersonation";

export type ImpersonationSession = {
  role: "student" | "partner";
  name: string;
  email: string;
  userId: number;
  expiresAt: number;
};

const canUseStorage = (): boolean => typeof window !== "undefined";

export const setImpersonationSession = (session: ImpersonationSession | null): void => {
  if (!canUseStorage()) return;
  if (session) {
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(session));
  } else {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  }
};

export const getImpersonationSession = (): ImpersonationSession | null => {
  if (!canUseStorage()) return null;
  const raw = sessionStorage.getItem(IMPERSONATION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ImpersonationSession;
    // A stale tab should fall back to the normal admin session rather than
    // presenting an expired impersonation as if it were live.
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
      setImpersonationSession(null);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    return null;
  }
};

export const isImpersonating = (): boolean => getImpersonationSession() !== null;

/**
 * The token for an impersonated tab lives in sessionStorage; everywhere else
 * it stays in localStorage. Callers must go through this rather than reaching
 * into either store directly.
 */
export const getAuthToken = (): string | null => {
  if (!canUseStorage()) return null;
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
};

export const setAuthToken = (token: string | null): void => {
  if (!canUseStorage()) return;
  const impersonating = isImpersonating();

  if (token) {
    // Normal sessions keep the existing localStorage behaviour so they survive
    // the tab closing; only an impersonated session is confined to this tab.
    if (impersonating) {
      sessionStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.setItem(TOKEN_KEY, token);
    }
    return;
  }

  sessionStorage.removeItem(TOKEN_KEY);
  // localStorage is shared with the administrator's own tab, so an
  // impersonated tab must never clear it: that would log the admin out.
  if (!impersonating) {
    localStorage.removeItem(TOKEN_KEY);
  } else {
    setImpersonationSession(null);
  }
};

const PORTAL_PATH: Record<string, string> = {
  student: "/student/dashboard",
  partner: "/partner/dashboard",
};

/**
 * Hand the freshly minted token to a new tab through the URL fragment, which
 * never reaches the server or its logs. The receiving tab moves it into
 * sessionStorage and strips the fragment from history immediately, so the token
 * is not left sitting in the address bar or in browser history.
 */
export const launchImpersonation = (
  token: string,
  session: Omit<ImpersonationSession, "expiresAt"> & { expiresIn?: number },
): void => {
  if (!canUseStorage()) return;
  const payload = {
    ...session,
    expiresAt: Date.now() + (session.expiresIn ?? 900) * 1000,
  };
  const encoded = encodeURIComponent(JSON.stringify(payload));
  const url = `${window.location.origin}${PORTAL_PATH[session.role] ?? "/"}#impersonate=${encodeURIComponent(token)}&s=${encoded}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Called once during bootstrap. If this tab was opened by launchImpersonation,
 * it adopts the token into sessionStorage, scrubs the fragment, and reloads
 * into the target portal so the app boots with a clean URL and the right role.
 */
export const consumeImpersonationFragment = (): boolean => {
  if (!canUseStorage()) return false;
  const hash = window.location.hash;
  if (!hash.startsWith("#impersonate=")) return false;

  const params = new URLSearchParams(hash.slice(1));
  const token = params.get("impersonate");
  const rawSession = params.get("s");
  if (!token || !rawSession) return false;

  try {
    const parsed = JSON.parse(decodeURIComponent(rawSession)) as ImpersonationSession;
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(parsed));
    const path = PORTAL_PATH[parsed.role] ?? "/";
    // replace, not assign, so the token-bearing URL leaves the history stack.
    window.location.replace(`${path}?impersonating=1`);
    return true;
  } catch {
    return false;
  }
};

/** Leave the impersonated session and return to the admin directory. */
export const exitImpersonation = (): void => {
  setImpersonationSession(null);
  window.location.replace("/admin/students");
};
